import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Settings, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const CATEGORY_LABEL = {
  signal_alerts: 'Signal alert',
  scheduled_reports: 'Scheduled report',
  broadcasts: 'Platform announcement',
  trial_warnings: 'Trial reminder',
};

const UnsubscribedPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [resubscribing, setResubscribing] = useState(false);
  const [resubMessage, setResubMessage] = useState('');

  const status = params.get('status') || 'invalid';
  const cat = params.get('cat') || '';
  const token = params.get('token') || '';
  const catLabel = CATEGORY_LABEL[cat] || cat || 'this category';

  useEffect(() => {
    document.title = status === 'ok'
      ? `Unsubscribed — dotbox.ai`
      : `Unsubscribe — dotbox.ai`;
  }, [status]);

  const handleResubscribe = async () => {
    setResubscribing(true);
    setResubMessage('');
    try {
      navigate('/login?redirect=/dashboard/settings#notifications');
    } catch {
      setResubMessage('Could not reach the server. Please sign in and update preferences manually.');
    } finally {
      setResubscribing(false);
    }
  };

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-[#0F1730] flex items-center justify-center px-4" data-testid="unsubscribed-invalid">
        <Card className="max-w-md w-full bg-[#1A2440] border-[#2A3654]">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center mb-3">
              <AlertTriangle className="h-6 w-6 text-amber-400" />
            </div>
            <h1 className="text-xl font-semibold text-white">Invalid unsubscribe link</h1>
          </CardHeader>
          <CardContent className="text-center text-slate-300 text-sm">
            <p className="mb-4">
              This unsubscribe link is invalid or has expired. You can manage all your email
              preferences from your account settings.
            </p>
            <Button
              onClick={() => navigate('/login?redirect=/dashboard/settings#notifications')}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="open-settings-button"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage preferences
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1730] flex items-center justify-center px-4" data-testid="unsubscribed-ok">
      <Card className="max-w-md w-full bg-[#1A2440] border-[#2A3654]">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white">
            You&apos;ve been unsubscribed
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            You will no longer receive <span className="text-white font-medium">{catLabel}</span> emails from dotbox.ai.
          </p>
        </CardHeader>
        <CardContent className="text-slate-300 text-sm">
          <div className="bg-[#0F1730] border border-[#2A3654] rounded-lg p-4 mb-5">
            <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              Still receiving important emails
            </p>
            <p className="text-xs text-slate-500 leading-relaxed">
              For your safety, you&apos;ll continue to receive transactional emails like password resets,
              login alerts, billing receipts, and account changes. These cannot be disabled.
            </p>
          </div>

          <div className="space-y-2">
            <Button
              onClick={() => navigate('/login?redirect=/dashboard/settings#notifications')}
              className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="manage-preferences-button"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage all notification preferences
            </Button>
            <Button
              variant="ghost"
              className="w-full text-slate-400 hover:text-white hover:bg-[#2A3654]"
              disabled={resubscribing}
              onClick={handleResubscribe}
              data-testid="resubscribe-button"
            >
              Re-subscribe to {catLabel} emails
            </Button>
          </div>

          {resubMessage && (
            <p className="text-xs text-amber-400 mt-3 text-center">{resubMessage}</p>
          )}

          <p className="text-xs text-slate-600 text-center mt-6">
            dotbox.ai · token: <span className="font-mono">{token.slice(0, 8)}...</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnsubscribedPage;
